import {
  useNotify,
  useRedirect,
} from "react-admin";
import {
  AppCreate,
  AppSimpleForm,
  AppFormToolbar,
  AppFormSection,
  SectionHeader,
  AppFormContent,
  AppTextInput,
  AppDateTimeInputDe,
  AppReferenceInput,
  AppRadioButtonGroupInput
} from "@/adapters/inbound/layout";
import { jobIntakeFormSchema, type JobIntakeFormInput } from "../schemas";
import { useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDI } from "@/infrastructure/di/useDI";
import { toCreateJobIntakeCommand } from "../mappers";

export const defaultJobIntakeFormValues: JobIntakeFormInput = {
  job: {
    title: "",
    description: "",
    notes: "",
    plannedStartAt: null,
    assignedPersonId: "",
  },
  client: {
    mode: "existing",
    clientId: "",
  },
  constructionSite: {
    mode: "existing",
    constructionSiteId: "",
  },
};

function ClientSection() {
  const mode = useWatch<JobIntakeFormInput>({ name: "client.mode" });

  return (
    <>
      <AppRadioButtonGroupInput
        source="client.mode"
        label="Kunde"
        choices={[
          { id: "existing", name: "Bestehenden Kunden wählen" },
          { id: "new", name: "Neuen Kunden anlegen" },
        ]}
      />

      {mode === "existing" ? (
        <AppReferenceInput
          source="client.clientId"
          reference="clients"
          label="Kunde"
          isRequired
          autocompleteProps={{
            optionText: "name",
            fullWidth: true,
          }}
        />
      ) : (
        <>
          <AppTextInput source="client.name" label="Name" required fullWidth />
          <AppTextInput
            source="client.contactName"
            label="Ansprechpartner"
            fullWidth
          />
          <AppTextInput source="client.phone" label="Telefon" fullWidth />
          <AppTextInput source="client.email" label="E-Mail" fullWidth />
          <AppTextInput
            source="client.address.label"
            label="Adresszusatz"
            fullWidth
          />
          <AppTextInput
            source="client.address.street"
            label="Straße"
            required
            fullWidth
          />
          <AppTextInput
            source="client.address.streetNumber"
            label="Nr."
            required
          />
          <AppTextInput source="client.address.zip" label="PLZ" required />
          <AppTextInput
            source="client.address.city"
            label="Ort"
            required
            fullWidth
          />
          <AppTextInput
            source="client.address.country"
            label="Land"
            required
            defaultValue="DE"
          />
        </>
      )}
    </>
  );
}

function ConstructionSiteSection() {
  const clientMode = useWatch<JobIntakeFormInput>({ name: "client.mode" });
  const siteMode = useWatch<JobIntakeFormInput>({
    name: "constructionSite.mode",
  });
  const clientId = useWatch<JobIntakeFormInput>({ name: "client.clientId" });

  const siteChoices =
    clientMode === "existing"
      ? [
          { id: "existing", name: "Bestehenden Einsatzort wählen" },
          { id: "client_address", name: "Kundenadresse verwenden" },
          { id: "new_address", name: "Andere Einsatzadresse eingeben" },
        ]
      : [
          { id: "client_address", name: "Kundenadresse verwenden" },
          { id: "new_address", name: "Andere Einsatzadresse eingeben" },
        ];

  return (
    <>
      <AppRadioButtonGroupInput
        source="constructionSite.mode"
        label="Einsatzort"
        choices={siteChoices}
      />

      {siteMode === "existing" && clientMode === "existing" ? (
        <AppReferenceInput
          source="constructionSite.constructionSiteId"
          reference="constructionSites"
          label="Einsatzort"
          filter={{ clientId }}
          isRequired
          autocompleteProps={{
            optionText: "label",
            fullWidth: true,
          }}
        />
      ) : null}

      {siteMode === "client_address" ? (
        <AppTextInput
          source="constructionSite.label"
          label="Bezeichnung des Einsatzorts"
          helperText="Optional – wird automatisch vorbelegt, falls leer"
          fullWidth
        />
      ) : null}

      {siteMode === "new_address" ? (
        <>
          <AppTextInput
            source="constructionSite.label"
            label="Bezeichnung des Einsatzorts"
            fullWidth
          />
          <AppTextInput
            source="constructionSite.address.label"
            label="Adresszusatz"
            fullWidth
          />
          <AppTextInput
            source="constructionSite.address.street"
            label="Straße"
            required
            fullWidth
          />
          <AppTextInput
            source="constructionSite.address.streetNumber"
            label="Nr."
            required
          />
          <AppTextInput
            source="constructionSite.address.zip"
            label="PLZ"
            required
          />
          <AppTextInput
            source="constructionSite.address.city"
            label="Ort"
            required
            fullWidth
          />
          <AppTextInput
            source="constructionSite.address.country"
            label="Land"
            required
            defaultValue="DE"
          />
        </>
      ) : null}
    </>
  );
}

export function JobCreate() {
  const notify = useNotify();
  const redirect = useRedirect();
  const { createJobIntake } = useDI();


  const onSubmit = async (values: any) => {
    try {
      const cmd = toCreateJobIntakeCommand(values);
      const result = await createJobIntake.execute(cmd);
      notify("Auftrag erstellt", { type: "success" });
      redirect("show", "jobs", result.id);
    } catch (error: any) {
      notify(error?.message ?? "Auftrag konnte nicht erstellt werden", {
        type: "error",
      });
    }
  };

  return (
    <AppCreate>
      <AppSimpleForm
        defaultValues={defaultJobIntakeFormValues}
        resolver={zodResolver(jobIntakeFormSchema) as any}
        mode="onBlur"
        shouldUnregister
        toolbar={<AppFormToolbar showDelete={false} />}
        onSubmit={onSubmit}
      >
        <AppFormSection>
          <SectionHeader
            title="Kunde und Einsatzort"
            subtitle="Wählen Sie vorhandene Daten oder erfassen Sie neue direkt hier."
          />
          <AppFormContent>
            <ClientSection />
            <ConstructionSiteSection />
          </AppFormContent>
        </AppFormSection>

        <AppFormSection>
          <SectionHeader
            title="Auftrag"
            subtitle="Erfassen Sie die wichtigsten Auftragsdaten."
          />
          <AppFormContent>
            <AppTextInput
              source="job.title"
              label="Titel"
              required
              autoFocus
              fullWidth
            />
            <AppDateTimeInputDe
              source="job.plannedStartAt"
              label="Geplanter Start"
              fullWidth
            />
            <AppReferenceInput
              source="job.assignedPersonId"
              reference="assignees"
              label="Zugewiesen"
              autocompleteProps={{
                optionText: "displayName",
                fullWidth: true,
              }}
            />
            <AppTextInput
              source="job.description"
              label="Beschreibung"
              multiline
              minRows={3}
              fullWidth
            />
            <AppTextInput
              source="job.notes"
              label="Notizen"
              multiline
              minRows={3}
              fullWidth
            />
          </AppFormContent>
        </AppFormSection>
      </AppSimpleForm>
    </AppCreate>
  );
}
